import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, CheckCircle2, FileSignature, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AgencyContractSign() {
  const { token } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [step, setStep] = useState('review'); // review | otp | done
  const [legalName, setLegalName] = useState('');
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/agency-tx/contract/${token}`);
      setC(r.data);
      if (r.data.status === 'signed') setStep('done');
    } catch { setError('This contract could not be found.'); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const requestOtp = async () => {
    if (!legalName.trim()) { setError('Enter your full legal name'); return; }
    if (!consent) { setError('Please consent to sign electronically'); return; }
    setError(''); setBusy(true);
    try {
      const r = await axios.post(`${API}/public/agency-tx/contract/${token}/otp`);
      setSentTo(r.data.destination_masked || 'your email');
      setStep('otp');
    } catch (e) { setError(e?.response?.data?.detail || 'Could not send code'); }
    finally { setBusy(false); }
  };

  const sign = async () => {
    setError(''); setBusy(true);
    try {
      await axios.post(`${API}/public/agency-tx/contract/${token}/sign`, { code: code.trim(), legal_name: legalName.trim(), consent });
      setStep('done'); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'object' ? 'That code is invalid or expired.' : (d || 'Signing failed'));
    } finally { setBusy(false); }
  };

  if (loading) return <Center><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></Center>;
  if (error && !c) return <Center><div className="text-slate-500">{error}</div></Center>;

  return (
    <div className="min-h-screen bg-slate-100 py-10 px-4" data-testid="public-contract-sign">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="bg-slate-900 text-white px-6 py-5">
          <div className="text-sm opacity-70">{c.agency_name}</div>
          <div className="flex items-center gap-2 mt-1"><FileSignature className="w-5 h-5" /><h1 className="text-xl font-bold">{c.title || c.contract_number}</h1></div>
        </div>

        {step === 'done' ? (
          <div className="p-8 text-center" data-testid="contract-signed-state">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h2 className="text-xl font-bold mt-3">Signed & complete</h2>
            <p className="text-slate-500 mt-1">A copy with the signature certificate has been recorded. Thank you!</p>
            {c.executed_copy_hash && <p className="text-xs text-slate-400 mt-3 break-all">Document hash: {c.executed_copy_hash}</p>}
          </div>
        ) : (
          <div className="p-6">
            <div className="prose prose-sm max-w-none border rounded-lg p-4 max-h-[46vh] overflow-y-auto bg-slate-50" data-testid="contract-body" dangerouslySetInnerHTML={{ __html: c.body_html }} />

            {step === 'review' && (
              <div className="mt-5 space-y-3">
                <div><label className="text-sm font-medium">Your full legal name</label><Input value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="e.g. Taylor Morgan Client" data-testid="legal-name-input" /></div>
                <label className="flex items-start gap-2 text-sm text-slate-600">
                  <Checkbox checked={consent} onCheckedChange={setConsent} data-testid="consent-checkbox" />
                  <span>I agree to sign this agreement electronically and that my electronic signature is legally binding under ESIGN/UETA.</span>
                </label>
                {error && <div className="text-sm text-rose-600">{error}</div>}
                <Button className="w-full bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={requestOtp} data-testid="request-otp-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-2" />Continue to verify & sign</>}
                </Button>
              </div>
            )}

            {step === 'otp' && (
              <div className="mt-5 space-y-3">
                <p className="text-sm text-slate-600">We sent a 6-digit verification code to <strong>{sentTo}</strong>. Enter it below to complete your signature.</p>
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6} className="text-center text-2xl tracking-widest" data-testid="otp-input" />
                {error && <div className="text-sm text-rose-600">{error}</div>}
                <Button className="w-full bg-teal-600 hover:bg-teal-700" disabled={busy || code.length < 4} onClick={sign} data-testid="sign-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Sign agreement'}
                </Button>
                <button className="text-xs text-slate-400 w-full" onClick={requestOtp} data-testid="resend-otp-btn">Resend code</button>
              </div>
            )}
          </div>
        )}
        <div className="px-6 py-3 bg-slate-50 text-center text-xs text-slate-400">Secure e-signature powered by iEvntür</div>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="min-h-screen flex items-center justify-center bg-slate-100">{children}</div>;
